import React, { useEffect, useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FiMove, FiEdit2, FiTrash2, FiCheck, FiX } from "react-icons/fi";
import { Spinner } from "@chakra-ui/react";
import { CompactTextInput } from "newui/components/ui/InputWithLabel";
import type { ComplianceQuestion } from "newui/features/Settings/types/ComplianceQuestion.type";

export interface SortableQuestionRowProps {
	question: ComplianceQuestion;
	isSaving?: boolean;
	onEdit: (id: number, question: string) => void;
	onDelete: (id: number) => void;
}

export const SortableQuestionRow = ({
	question,
	isSaving = false,
	onEdit,
	onDelete,
}: SortableQuestionRowProps) => {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: question.id });

	const [isEditing, setIsEditing] = useState(false);
	const [draft, setDraft] = useState(question.question);

	// Keep the local draft in sync if the underlying question changes
	// while the row is not being edited.
	useEffect(() => {
		if (!isEditing) {
			setDraft(question.question);
		}
	}, [question.question, isEditing]);

	const style: React.CSSProperties = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	};

	const trimmed = draft.trim();
	const canSave = trimmed.length > 0 && trimmed !== question.question;

	const handleStartEdit = () => {
		setDraft(question.question);
		setIsEditing(true);
	};

	const handleCancel = () => {
		setDraft(question.question);
		setIsEditing(false);
	};

	const handleConfirm = () => {
		if (!canSave) {
			setIsEditing(false);
			return;
		}
		onEdit(question.id, trimmed);
		setIsEditing(false);
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			e.preventDefault();
			handleConfirm();
		}
		if (e.key === "Escape") {
			e.preventDefault();
			handleCancel();
		}
	};

	return (
		<div
			ref={setNodeRef}
			style={style}
			data-testid="compliance-question-row"
			className="flex items-center gap-3 border-b border-gray-100 bg-white px-3 py-3"
		>
			<button
				type="button"
				aria-label="Drag to reorder question"
				data-testid="compliance-question-drag-handle"
				className="flex h-6 w-6 shrink-0 cursor-grab items-center justify-center rounded text-gray-400 hover:text-gray-600"
				{...attributes}
				{...listeners}
			>
				<FiMove size={16} />
			</button>

			<div className="min-w-0 flex-1">
				{isEditing ? (
					<CompactTextInput
						autoFocus
						value={draft}
						hasError={trimmed.length === 0}
						data-testid="compliance-question-edit-input"
						onChange={(e) => setDraft(e.target.value)}
						onKeyDown={handleKeyDown}
					/>
				) : (
					<span
						className="block truncate text-sm text-gray-800"
						data-testid="compliance-question-text"
						title={question.question}
					>
						{question.question}
					</span>
				)}
			</div>

			{isSaving && <Spinner size="sm" color="blue.500" />}

			{isEditing ? (
				<>
					<button
						type="button"
						aria-label="Save question"
						data-testid="compliance-question-save-btn"
						disabled={!canSave}
						className="flex h-7 w-7 items-center justify-center rounded text-green-600 hover:bg-green-50 disabled:cursor-not-allowed disabled:text-gray-300"
						onClick={handleConfirm}
					>
						<FiCheck size={16} />
					</button>
					<button
						type="button"
						aria-label="Cancel editing question"
						data-testid="compliance-question-cancel-btn"
						className="flex h-7 w-7 items-center justify-center rounded text-gray-500 hover:bg-gray-100"
						onClick={handleCancel}
					>
						<FiX size={16} />
					</button>
				</>
			) : (
				<>
					<button
						type="button"
						aria-label="Edit question"
						data-testid="compliance-question-edit-btn"
						className="flex h-7 w-7 items-center justify-center rounded text-gray-500 hover:bg-gray-100"
						onClick={handleStartEdit}
					>
						<FiEdit2 size={16} />
					</button>
					<button
						type="button"
						aria-label="Delete question"
						data-testid="compliance-question-delete-btn"
						className="flex h-7 w-7 items-center justify-center rounded text-red-500 hover:bg-red-50"
						onClick={() => onDelete(question.id)}
					>
						<FiTrash2 size={16} />
					</button>
				</>
			)}
		</div>
	);
};
